/** @format — Single port option card (themed border, hover bg, CTA hover) */

import React, { useState } from "react";
import { Ship, MapPin, Clock, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import PortBadge from "./PortBadge";
import {
  getPortCardSurfaceClasses,
  resolvePortOptionPresentation,
} from "./portBadgeUtils";

const CARD_WIDTH =
  "w-[240px] max-w-[min(240px,calc(100vw-2rem))] min-w-[240px] sm:min-w-[240px]";

function getBreakdownPanelClass(theme) {
  if (theme?.breakdownBg) {
    return `${theme.breakdownBg} border border-black/5`;
  }
  return "bg-[#F9FAFB] border border-[#e4e7ec]";
}

function getCtaLabel(option) {
  const name = option.portName ?? option.label ?? "port";
  return `Choose ${name}`;
}

function formatMoney(n, currency = "USD") {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(n));
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

function DetailRow({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 min-h-[20px]">
      <span className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
        <Icon className="w-3 h-3 text-[#737373]" strokeWidth={1.5} aria-hidden />
      </span>
      <span className="text-xs leading-none text-[#1d2739] truncate">{children}</span>
    </div>
  );
}

export default function PortOptionCard({ option, onSelect, disabled }) {
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const currency = option.currency ?? "USD";
  const { badges, theme } = resolvePortOptionPresentation(option);

  const total =
    option.totalCost ??
    (option.truckingCost != null && option.oceanCost != null
      ? Number(option.truckingCost) + Number(option.oceanCost)
      : null);

  const hasBreakdown =
    option.truckingCost != null && option.oceanCost != null && total != null;

  const ctaLabel = getCtaLabel(option);
  const breakdownPanelClass = getBreakdownPanelClass(theme);

  const cardSurface = getPortCardSurfaceClasses(theme);

  const focusRing = theme?.focusRing ?? "focus-visible:outline-blue-600";
  const ctaClass = theme
    ? `${theme.ctaDefault} ${theme.ctaActive} ${theme.ctaPressed}`
    : "bg-white border border-neutral-300 text-[#0a0a0a] hover:bg-[#F9FAFB] hover:border-[#98A2B3]";

  const handleCardActivate = () => {
    if (disabled) return;
    onSelect?.(option.id);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect?.(option.id);
    }
  };

  const toggleBreakdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBreakdownOpen((open) => !open);
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={`Select ${option.label ?? option.portName ?? "port"}`}
      aria-disabled={disabled}
      onClick={handleCardActivate}
      onKeyDown={handleKeyDown}
      className={`group flex flex-col flex-shrink-0 snap-start ${CARD_WIDTH} rounded-lg border relative overflow-visible text-left cursor-pointer transition-[background-color,box-shadow] duration-200 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${focusRing} ${cardSurface} gap-2 p-4 ${
        disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
      }`}
    >
      {badges.length > 0 ? (
        <div className="flex flex-wrap items-start gap-1.5 w-full pointer-events-none">
          {badges.map((badgeId) => (
            <PortBadge key={badgeId} badgeId={badgeId} />
          ))}
        </div>
      ) : null}

      <div className="flex flex-col gap-2.5 flex-1 min-h-0 w-full pointer-events-none">
        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-medium text-left text-[#11121a] line-clamp-2 leading-snug">
            {option.label}
          </p>

          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-semibold leading-none text-[#0a0a0a]">
              {formatMoney(total, currency)}
            </p>
            {hasBreakdown ? (
              <button
                type="button"
                data-card-action="breakdown-toggle"
                disabled={disabled}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={toggleBreakdown}
                className="inline-flex items-center justify-center p-0.5 rounded hover:bg-black/5 pointer-events-auto flex-shrink-0"
                aria-expanded={breakdownOpen}
                aria-label={breakdownOpen ? "Hide cost breakdown" : "Show cost breakdown"}
              >
                {breakdownOpen ? (
                  <ChevronUp className="w-3.5 h-3.5 text-[#737373]" aria-hidden />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-[#737373]" aria-hidden />
                )}
              </button>
            ) : null}
          </div>

          {hasBreakdown && breakdownOpen ? (
            <div
              className={`flex flex-col gap-2 px-2 py-2 rounded ${breakdownPanelClass}`}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center gap-3">
                <span className="text-[10px] leading-none text-[#475367]">Trucking</span>
                <span className="text-[10px] font-semibold leading-none uppercase text-[#1d2739]">
                  {formatMoney(option.truckingCost, currency)}
                </span>
              </div>
              <div className="flex justify-between items-center gap-3">
                <span className="text-[10px] leading-none text-[#475367]">Ocean</span>
                <span className="text-[10px] font-semibold leading-none uppercase text-[#1d2739]">
                  {formatMoney(option.oceanCost, currency)}
                </span>
              </div>
            </div>
          ) : null}

          {option.tag ? (
            <span className="text-[11px] leading-none text-[#667185] font-medium">{option.tag}</span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          {option.vessel ? <DetailRow icon={Ship}>{option.vessel}</DetailRow> : null}
          {formatDate(option.departureDate) ? (
            <DetailRow icon={Calendar}>Departs {formatDate(option.departureDate)}</DetailRow>
          ) : null}
          {formatDate(option.etaDate) ? (
            <DetailRow icon={Clock}>ETA {formatDate(option.etaDate)}</DetailRow>
          ) : null}
          {option.route && !option.vessel ? (
            <DetailRow icon={MapPin}>{option.route}</DetailRow>
          ) : null}
        </div>
      </div>

      <span
        className={`mt-auto w-full min-w-0 flex justify-center items-center px-3 py-2 rounded-full text-sm font-medium leading-none flex-shrink-0 transition-colors pointer-events-auto ${ctaClass}`}
      >
        <span className="block w-full min-w-0 truncate text-center" title={ctaLabel}>
          {ctaLabel}
        </span>
      </span>
    </div>
  );
}
